import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import apiFunctions from "../../services/api-service";
import { formatDateTime } from "../../utils/HelperFunctions";
import useToast from "../../components/common/useToast";
import "../../assets/css/datatable.css";
import { Button } from "primereact/button";
import { Paginator, PaginatorCurrentPageReportOptions, PaginatorRowsPerPageDropdownOptions } from "primereact/paginator";
import { BellIcon } from "@heroicons/react/24/outline";
import { Skeleton } from "primereact/skeleton";
import WhatsAppIcon from "../../components/common/WhatsAppIcon";
import ConfirmActionPopup from "../../components/modal-popup/confirm-action-popup";

const NotificationLogsPage = () => {
    const { id } = useParams();
    const { showToast }: any = useToast();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [sendStatus, setSendStatus] = useState("");
    const [search, setSearch] = useState("");
    const [totalRecords, setTotalRecords] = useState(0);

    const [itemsPerPage, setItemsPerPage] = useState<any>(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [skipData, setSkipData] = useState(0);
    const [totalPages, settotalPages] = useState<any>(0);

    const [waSendingId, setWaSendingId] = useState("");
    const [selectedLogs, setSelectedLogs] = useState<any[]>([]);
    const [waBulkSending, setWaBulkSending] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState("");
    const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);

    const startIndex = currentPage * itemsPerPage;

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fetchLogs = async () => {
        try {
            setLoading(true);

            const res = await apiFunctions.get("/activeusers/scheduled-notification-logs", {
                params: {
                    scheduleId: id,
                    page: currentPage + 1,
                    limit: itemsPerPage,
                    send_status: sendStatus,
                    search
                }
            });

            if (res.data.success) {
                setData(res.data.data);
                setTotalRecords(res.data.totalRecords);
                settotalPages(res.data.totalPages || 1);
            }
        } catch (err) {
            showToast({
                severity: "error",
                summary: "Error",
                detail: "Failed to load logs"
            });
        } finally {
            setLoading(false);
        }
    };

    const statusBody = (row: any) => {
        const color =
            row.send_status === "sent"
                ? "bg-green-100 text-green-700"
                : row.send_status === "failed"
                    ? "bg-red-100 text-red-700"
                    : "bg-yellow-100 text-yellow-700";

        return (
            <span className={`px-2 py-1 rounded text-xs font-semibold ${color}`}>
                {row.send_status}
            </span>
        );
    };

// Step 1: single-row send — ask for confirmation first
    const sendWhatsAppToUser = (row: any) => {
        setConfirmMessage(`Send WhatsApp to ${row.username || "this user"}?`);
        setConfirmAction(() => () => doSendWhatsAppToUser(row));
        setConfirmVisible(true);
    };

    // Manual WhatsApp fallback for a failed push — sends the "update"
    // template to this user ({{1}} name is filled by the backend)
    const doSendWhatsAppToUser = async (row: any) => {
        try {
            setWaSendingId(row._id);

            const res = await apiFunctions.post("/activeusers/whatsapp-notifications", {
                userIds: [row.userid],
                template: "update",
                params: []
            });

            if (res.data.success) {
                const failed = res.data.summary?.failed || 0;
                showToast({
                    severity: failed > 0 ? "error" : "success",
                    summary: failed > 0 ? "WhatsApp Failed" : "WhatsApp Sent",
                    detail: failed > 0
                        ? `Could not send WhatsApp to ${row.username || "user"}`
                        : `WhatsApp sent to ${row.username || "user"}`,
                    life: 4000
                });
            }
        } catch (err) {
            showToast({
                severity: "error",
                summary: "Error",
                detail: "Failed to send WhatsApp"
            });
        } finally {
            setWaSendingId("");
            setConfirmVisible(false);
        }
    };

    // Step 1: bulk send — ask for confirmation first, showing the recipient count
    const sendWhatsAppToSelected = () => {
        if (!selectedLogs.length) return;
        const userIds = Array.from(new Set(selectedLogs.map((l) => l.userid).filter(Boolean)));
        setConfirmMessage(`This WhatsApp message will be sent to ${userIds.length} user${userIds.length === 1 ? "" : "s"}. Are you sure?`);
        setConfirmAction(() => () => doSendWhatsAppToSelected());
        setConfirmVisible(true);
    };

    // Bulk WhatsApp ("update" template) to every selected user — big batches
    // run in the backend's background mode
    const doSendWhatsAppToSelected = async () => {
        if (!selectedLogs.length) return;

        // one user can have multiple log rows — send once per user
        const userIds = Array.from(new Set(selectedLogs.map((l) => l.userid).filter(Boolean)));

        try {
            setWaBulkSending(true);

            const res = await apiFunctions.post("/activeusers/whatsapp-notifications", {
                userIds,
                template: "update",
                params: []
            });

            if (res.data.success) {
                if (res.data.summary) {
                    const { total, sent, failed } = res.data.summary;
                    showToast({
                        severity: failed > 0 ? "warn" : "success",
                        summary: "WhatsApp Report",
                        detail: `Total: ${total}, Sent: ${sent}, Failed: ${failed}`,
                        life: 5000
                    });
                } else {
                    showToast({
                        severity: "info",
                        summary: "WhatsApp Started",
                        detail: `Sending WhatsApp to ${userIds.length} users in background`,
                        life: 4000
                    });
                }
                setSelectedLogs([]);
            }
        } catch (err) {
            showToast({
                severity: "error",
                summary: "Error",
                detail: "Failed to send WhatsApp"
            });
        } finally {
            setWaBulkSending(false);
            setConfirmVisible(false);
        }
    };

    const toggleLogSelection = (row: any) => {
        setSelectedLogs((prev) =>
            prev.some((l) => l._id === row._id)
                ? prev.filter((l) => l._id !== row._id)
                : [...prev, row]
        );
    };

    const actionBody = (row: any) => {
        if (row.send_status !== "failed") return null;

        return (
            <Button
                icon={<WhatsAppIcon size={20} />}
                className="w-11 h-11 flex items-center justify-center"
                size="small"
                severity="success"
                tooltip="Send WhatsApp (update)"
                tooltipOptions={{ position: "left" }}
                loading={waSendingId === row._id}
                onClick={() => sendWhatsAppToUser(row)}
            />
        );
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(0);
    };

    useEffect(() => {
        setSkipData(currentPage * itemsPerPage);
    }, [currentPage, itemsPerPage]);

    useEffect(() => {
        fetchLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipData, itemsPerPage, search, sendStatus, id]);

    const header = (
        <div className="grid grid-cols-12 gap-3 items-start sm:items-center table-header mb-4">

            <div className="col-span-12 sm:col-span-6">
                <h2 className="card-title m-0 text-lg sm:text-xl">
                    Notification Delivery Logs
                </h2>
                {selectedLogs.length > 0 && (
                    <span className="text-sm font-medium" style={{ color: "#16a34a" }}>
                        {selectedLogs.length} selected
                    </span>
                )}
            </div>

            <div className="col-span-12 sm:col-span-6 flex flex-col sm:flex-row sm:items-center justify-start sm:justify-end gap-2">

                {selectedLogs.length > 0 && (
                    <Button
                        severity="success"
                        loading={waBulkSending}
                        onClick={sendWhatsAppToSelected}
                        className="flex-shrink-0"
                        style={{ whiteSpace: "nowrap" }}
                    >
                        <span className="flex items-center gap-2">
                            <WhatsAppIcon size={16} />
                            Send WhatsApp ({selectedLogs.length})
                        </span>
                    </Button>
                )}

                <InputText
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name, surname, mobile, city"
                    style={{ minWidth: "240px" }}
                />

                <select
                    value={sendStatus}
                    onChange={(e) => setSendStatus(e.target.value)}
                    className="border rounded px-3 py-2 min-w-[140px]"
                >
                    <option value="">All</option>
                    <option value="sent">Sent</option>
                    <option value="failed">Failed</option>
                    <option value="pending">Pending</option>
                </select>

            </div>
        </div>
    );

    const paginatoreTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
                { label: 200, value: 200 },
            ];

            return (
                <div className="paginator-dropdown">
                    <span className="mx-1">Show</span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsPerPageChange(e.value)}
                    />
                </div>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries-text mx-1">entries</span>;
        },
    };

    return (
        <div className="unlist-margin">
            <div>
                {isMobile ? (
                    <div className="bg-white w-full overflow-x-hidden box-border" style={{ borderRadius: "8px" }}>
                        <div className="p-3 pb-2">{header}</div>
                        <div className="flex flex-col gap-2 px-3 pb-3">
                            {loading ? (
                                Array.from({ length: itemsPerPage }, (_, i) => (
                                    <div key={i} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <Skeleton width="60%" height="1rem" className="mb-2" />
                                        <Skeleton width="40%" height="0.8rem" />
                                    </div>
                                ))
                            ) : data.length === 0 ? (
                                <div style={{ textAlign: "center", padding: "64px 0" }}>
                                    <BellIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                    <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No notification logs found</p>
                                </div>
                            ) : (
                                data.map((rowData: any, idx: number) => (
                                    <div key={rowData._id || idx} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <div className="flex items-start justify-between gap-2 w-full">
                                            <div className="flex items-start gap-2 flex-1 min-w-0">
                                                <input
                                                    type="checkbox"
                                                    className="mt-1"
                                                    checked={selectedLogs.some((l) => l._id === rowData._id)}
                                                    onChange={() => toggleLogSelection(rowData)}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className="m-0 font-semibold text-sm break-words" style={{ color: "#264164" }}>{rowData.username}</p>
                                                    <p className="m-0 text-xs break-words" style={{ color: "#9ca3af" }}>
                                                        {rowData.mobile}{rowData.city ? ` · ${rowData.city}` : ""}
                                                    </p>
                                                </div>
                                            </div>
                                            {statusBody(rowData)}
                                        </div>
                                        <div className="flex flex-col gap-1 mt-2 text-xs" style={{ color: "#4b5563" }}>
                                            <div className="break-words"><span className="font-medium">Title:</span> {rowData.title}</div>
                                            {rowData.error_message && <div className="break-words"><span className="font-medium">Error:</span> {rowData.error_message}</div>}
                                            <div>{formatDateTime(rowData.sent_at)}</div>
                                        </div>
                                        {rowData.send_status === "failed" && (
                                            <div className="flex justify-end mt-2">
                                                {actionBody(rowData)}
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                ) : (
                    <DataTable
                        lazy
                        scrollable
                        scrollHeight="calc(100vh - 240px)"
                        style={{ minHeight: "calc(100vh - 185px)", background: "#fff" }}
                        header={header}
                        value={
                            loading
                                ? Array.from({ length: itemsPerPage }, (_, i) => ({ _id: `_skeleton_${i}` }))
                                : data
                        }
                        dataKey="_id"
                        selectionMode="checkbox"
                        selection={loading ? [] : selectedLogs}
                        onSelectionChange={(e: any) => !loading && setSelectedLogs(e.value)}
                        resizableColumns
                        removableSort
                        rows={itemsPerPage}
                        emptyMessage={
                            <div style={{ textAlign: "center", padding: "64px 0" }}>
                                <BellIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No notification logs found</p>
                            </div>
                        }
                    >
                        {loading ? (
                            <Column headerStyle={{ width: "3rem" }} body={() => <Skeleton width="1.2rem" height="1.2rem" />} />
                        ) : (
                            <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
                        )}
                        <Column field="username" header="Full Name" body={(row) => loading ? <Skeleton /> : row.username} />
                        <Column field="mobile" header="Mobile" body={(row) => loading ? <Skeleton /> : row.mobile} />
                        <Column field="city" header="City" body={(row) => loading ? <Skeleton /> : row.city} />
                        <Column field="title" header="Title" body={(row) => loading ? <Skeleton /> : row.title} />
                        <Column header="Status" body={(row) => loading ? <Skeleton /> : statusBody(row)} />
                        <Column field="error_message" header="Error" body={(row) => loading ? <Skeleton /> : row.error_message} />
                        <Column
                            header="Sent At"
                            body={(row) =>
                                loading ? <Skeleton /> : formatDateTime(row.sent_at)
                            }
                        />
                        <Column
                            header="Action"
                            body={(row) => loading ? <Skeleton /> : actionBody(row)}
                            frozen
                            alignFrozen="right"
                            style={{ minWidth: "90px", background: "#fff" }}
                        />
                    </DataTable>
                )}
                {totalRecords > 0 && <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2 mt-2 sm:mt-0" style={{ borderRadius: "8px" }}>
                    {/* PrimeReact Paginator */}
                    <Paginator
                        template={paginatoreTemplate}
                        first={startIndex}
                        rows={itemsPerPage}
                        totalRecords={totalRecords}
                        onPageChange={(e) => {
                            setCurrentPage(e.page);
                            setItemsPerPage(e.rows);
                        }}
                    />

                    {/* Custom Pagination Buttons */}
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Button
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage === 0}
                        >
                            <span className="sm:hidden">Prev</span>
                            <span className="hidden sm:inline">Previous</span>
                        </Button>

                        <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                            <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                Page {currentPage + 1} of {totalPages || 1}
                            </span>
                        </Button>

                        <Button
                            onClick={() => handlePageChange(currentPage + 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage + 1 === (totalPages === 0 ? 1 : totalPages)}
                        >
                            Next
                        </Button>
                    </div>
                </div>}
            </div>
            <ConfirmActionPopup
                visible={confirmVisible}
                onHide={() => setConfirmVisible(false)}
                onConfirm={() => confirmAction && confirmAction()}
                header="Confirm WhatsApp Send"
                confirmLabel="Send"
                loading={!!waSendingId || waBulkSending}
                message={confirmMessage}
            />
        </div>
    );
};

export default NotificationLogsPage;